import { useCallback, useEffect, useMemo, useState } from 'react';
import { getContestLeaderboard } from '../../../../auth/_requests';
import type { ContestLeaderboardItem, ContestLeaderboardResponse } from '../../../../helpers/utils/types';

export type ContestLeaderboardState = {
  loading: boolean;
  items: ContestLeaderboardItem[];
  meta?: ContestLeaderboardResponse['meta'];
  error?: string;
};

export const useContestLeaderboard = (opts: {
  token?: string;
  enabled: boolean;
  limit?: number;
  includeOnlyLatestContest?: boolean;
}) => {
  const { token, enabled, limit = 10, includeOnlyLatestContest = true } = opts;
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ContestLeaderboardItem[]>([]);
  const [meta, setMeta] = useState<ContestLeaderboardResponse['meta'] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  const fetchLeaderboard = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(undefined);
    try {
      const res = await getContestLeaderboard(token, { limit, includeOnlyLatestContest });
      setItems(res?.data ?? []);
      setMeta(res?.meta);
    } catch (e) {
      console.error('Error fetching contest leaderboard for rewards page:', e);
      setItems([]);
      setMeta(undefined);
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [token, limit, includeOnlyLatestContest]);

  useEffect(() => {
    if (!enabled) return;
    fetchLeaderboard();
  }, [enabled, fetchLeaderboard]);

  return useMemo(() => ({ loading, items, meta, error }), [loading, items, meta, error]);
};

